import Event from './Event'

/**
 * Formats, sorts, filters a post and its children to create a list.
 * For example, can be used to create menus.
 */
export default class EventList {
    constructor(events = [], options) {
        this.events = !events
            ? []
            : events.map(event => new Event(event).getEvent())
        this.setOptions(options)
        this.filter()
        this.sort()
    }

    /**
     * Sets the options to create the list.
     *
     * @param {object} options
     */
    setOptions(options) {
        this.shouldSort = options?.sort ?? false
        this.filters = options?.filters ?? []
        this.filters = [
            ...this.filters,
            this.filterByProperty('isPrivate', false),
            this.filterByProperty('inPast', false)
        ]
    }

    sort() {
        if (this.shouldSort) {
            this.sortEventsByDate()
        }
    }

    filter() {
        this.events = this.filters.reduce(
            (events, filterFn) => events.filter(filterFn),
            this.events,
        )
    }

    /**
     * Returns the event list based on the current options set.
     * @returns an array of events
     */
    getList() {
        return this.events
    }

    /**
     * Remaps a property to a new property name.
     *
     * @param {string} property The property to copy.
     * @param {string} name The name of the new property.
     * @returns EventList
     */
    remap(property, name) {
        this.events = this.events.map(event => {
            event[name] = event[property]
            return event
        })
        return this
    }

    /**
     * Sorts events by 'startDate' property.
     * Note: If startDate is in the past, a future end date
     * gets checked as well. If so, it will sort as if the
     * event is happening now.
     *
     * @param {array} events An array of event objects.
     */
    sortEventsByDate() {
        if (!Array.isArray(this.events)) {
            return
        }

        const validEvents = this.events.filter(event => {
            if (!event.hasOwnProperty('startDate')) return false

            const date = new Date(event.startDate.replace(/\s/, 'T'))

            if ('Invalid Date' == date) {
                return false
            }

            event.sortDate = date

            if (event.inPast) {
                if (event.hasOwnProperty('endDate')) {
                    const end = new Date(event.endDate.replace(/\s/, 'T'))

                    if ('Invalid Date' != end) {
                        event.sortDate = new Date()
                    }
                }
            }

            return true
        })

        this.events = validEvents.sort(
            (event1, event2) => event1.sortDate - event2.sortDate,
        )
    }

    /**
     * Filter the post by a specific property/value.
     *
     * @param {string} property The property to filter by
     * @param {*} value The value to check
     * @returns array
     */
    filterByProperty(property, value) {
        return function filterItemByProperty(post) {
            return post?.[property] === value
        }
    }
}
